import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Badge component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Border Radius: 6px (rounded-md)
 * - Padding: px-2 py-1
 * - Font: 12px/500 (Label/XSmall)
 * - Background: color/15% opacity
 * - Text: color-500 (light) / color-400 (dark)
 */
const badgeVariants = cva(
  // Base: 6px radius, 12px/500 font, subtle transition
  'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium transition-colors duration-100',
  {
    variants: {
      variant: {
        // Neutral variants
        default:
          'bg-grey-100 text-grey-700 dark:bg-grey-800 dark:text-grey-300',
        secondary:
          'bg-grey-200/50 text-grey-500 dark:bg-grey-700/50 dark:text-grey-400',
        outline:
          'border border-grey-200 text-grey-700 bg-transparent dark:border-grey-700 dark:text-grey-300',
        none:
          'bg-grey-200 text-grey-500 dark:bg-grey-700 dark:text-grey-400',

        // Semantic status variants (Hyperkit exact colors)
        success:
          'bg-green-500/15 text-green-500 dark:text-green-400',
        warning:
          'bg-orange-500/15 text-orange-500 dark:text-orange-400',
        destructive:
          'bg-red-500/15 text-red-500 dark:text-red-400',
        info:
          'bg-teal-500/15 text-teal-500 dark:text-teal-400',
        purple:
          'bg-purple-500/15 text-purple-500 dark:text-purple-400',

        // Hyperkit Status Badges (exact from Figma)
        pending:
          'bg-orange-500/15 text-orange-500 dark:text-orange-400',
        active:
          'bg-blue-500/15 text-blue-500 dark:text-blue-400',
        running:
          'bg-purple-500/15 text-purple-500 dark:text-purple-400',
        passed:
          'bg-green-500/15 text-green-500 dark:text-green-400',
        failed:
          'bg-red-500/15 text-red-500 dark:text-red-400',
        error:
          'bg-red-500/15 text-red-500 dark:text-red-400',
        canceled:
          'bg-grey-500/15 text-grey-500 dark:text-grey-400',
        archived:
          'bg-grey-500/15 text-grey-500 dark:text-grey-400',
        deleted:
          'bg-red-500/15 text-red-500 dark:text-red-400',
        request:
          'bg-orange-500/15 text-orange-500 dark:text-orange-400',
        'in-queue':
          'bg-blue-500/15 text-blue-500 dark:text-blue-400',

        // KYC-specific status variants
        approved:
          'bg-green-500/15 text-green-500 dark:text-green-400',
        'in-review':
          'bg-blue-500/15 text-blue-500 dark:text-blue-400',
        rejected:
          'bg-red-500/15 text-red-500 dark:text-red-400',
        escalated:
          'bg-red-500/15 text-red-500 dark:text-red-400',

        // Risk level variants
        'low-risk':
          'bg-green-500/15 text-green-500 dark:text-green-400',
        'medium-risk':
          'bg-orange-500/15 text-orange-500 dark:text-orange-400',
        'high-risk':
          'bg-red-500/15 text-red-500 dark:text-red-400',

        // Screening flag variants
        'flag-clear':
          'bg-green-500/15 text-green-500 dark:text-green-400 uppercase text-[11px] font-semibold tracking-wide',
        'flag-warning':
          'bg-orange-500/15 text-orange-500 dark:text-orange-400 uppercase text-[11px] font-semibold tracking-wide',
        'flag-danger':
          'bg-red-500/15 text-red-500 dark:text-red-400 uppercase text-[11px] font-semibold tracking-wide',
        'flag-info':
          'bg-blue-500/15 text-blue-500 dark:text-blue-400 uppercase text-[11px] font-semibold tracking-wide',

        // Priority variants
        vip:
          'bg-amber-500/15 text-amber-500 dark:text-amber-400 uppercase text-[11px] font-semibold',
        priority:
          'bg-blue-500/15 text-blue-500 dark:text-blue-400 uppercase text-[11px] font-semibold',

        // Init/New status
        init:
          'bg-grey-500/15 text-grey-500 dark:text-grey-400',
        muted:
          'bg-grey-200 text-grey-500 dark:bg-grey-700 dark:text-grey-400',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Badge = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
});
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
